/**
 * Horizontal center (px from the scaled preview frame’s left edge) so a fixed-width
 * arrow editor panel clears the rendered arrow column.
 */

export interface PreviewArrowDockOptions {
  panelWidth?: number;
  gap?: number;
}

export function computePreviewArrowDockCenterX(
  scaledFrameEl: HTMLElement,
  /** Root that contains `#reactant-molecule` etc. (#build-preview-export). */
  exportMountEl: HTMLElement,
  /** CSS width of the scaled preview frame (= build-preview-export-frame width). */
  scaledInnerWidthPx: number,
  opts?: PreviewArrowDockOptions,
): number | null {
  const panelW = opts?.panelWidth ?? 200;
  const gap = opts?.gap ?? 10;

  const arrowEl = exportMountEl.querySelector('.preview-paste-slot--arrow');
  if (!arrowEl || !(arrowEl instanceof HTMLElement)) return null;

  const fr = scaledFrameEl.getBoundingClientRect();
  const ar = arrowEl.getBoundingClientRect();
  if (!(fr.width > 1 && scaledInnerWidthPx > 1 && ar.width >= 1)) return null;

  const sx = scaledInnerWidthPx / fr.width;
  const arrowLeft = (ar.left - fr.left) * sx;
  const arrowRight = (ar.right - fr.left) * sx;

  const halfP = panelW / 2;
  const minC = halfP;
  const maxC = scaledInnerWidthPx - halfP;

  /** Panel right edge clears arrow’s left (+ gap); center sits just left of the arrow strip. */
  const leftDock = arrowLeft - gap - halfP;
  /** Panel left edge clears arrow’s right (+ gap). */
  const rightDock = arrowRight + gap + halfP;

  const fitsLeft = leftDock >= minC && leftDock <= maxC;
  const fitsRight = rightDock >= minC && rightDock <= maxC;

  if (fitsLeft && fitsRight) {
    const leftMargin = arrowLeft - (leftDock + halfP);
    const rightMargin = rightDock - halfP - arrowRight;
    return leftMargin >= rightMargin ? leftDock : rightDock;
  }
  if (fitsLeft) return leftDock;
  if (fitsRight) return rightDock;

  return Math.min(maxC, Math.max(minC, (leftDock + rightDock) / 2));
}
